import { Dispatch, SetStateAction, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import Input from 'common/ui/Input';
import Text from 'common/ui/Text';
import { Stack } from '../ui/Layout';
import { Colors } from 'styles';
import { Button } from 'common/ui/Button';
import { useMutation } from '@apollo/client';
import type {
  ToggleMultiFactorAuthMutation,
  ToggleMultiFactorAuthMutationVariables,
} from 'generated/graphql';
import { TOGGLE_MULTI_FACTOR_AUTH } from 'modules/Apollo/queries';
import { logger } from 'modules/logger';
import CircularProgress from '@mui/material/CircularProgress';
import { telemetryContextFor } from 'modules/Apollo/types';

const PasswordDialog = ({
  show,
  multiFactorAuthEnabled,
  onSuccess,
  onClose,
  setAuthenticatorToken,
}: {
  show: boolean;
  multiFactorAuthEnabled: boolean;
  onSuccess: (arg1: boolean) => void;
  onClose: () => void;
  setAuthenticatorToken: Dispatch<SetStateAction<null>>;
}): React.ReactElement => {
  const [password, setPassword] = useState('');
  const [toggleMultiFactorAuth, { loading, error }] = useMutation<
    ToggleMultiFactorAuthMutation,
    ToggleMultiFactorAuthMutationVariables
  >(TOGGLE_MULTI_FACTOR_AUTH, {
    context: APOLLO_QUERY_CONTEXT,
  });

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    await toggleMultiFactorAuth({
      variables: {
        multiFactorAuthEnabled: multiFactorAuthEnabled,
        password,
      },
      onCompleted(data: any): undefined {
        setAuthenticatorToken(data?.toggleMultiFactorAuth?.authenticatorToken);
        setPassword('');
        onSuccess(multiFactorAuthEnabled);
      },
      onError: (error) => {
        logger.debug(error);
      },
    });
  };

  return (
    <Dialog
      maxWidth="md"
      open={show}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      data-testid="password-dialog"
    >
      <DialogContent>
        <Stack css="padding: 20px 5px 25px 5px; width: 295px" vertical>
          <Stack>
            <Text variant="display2">Let's verify it's you.</Text>
          </Stack>
          {/* @ts-expect-error [EN-7967] - TS2322 - Type '(e: Event) => Promise<void>' is not assignable to type 'FormEventHandler<HTMLFormElement>'. */}
          <form onSubmit={onSubmit}>
            <Stack css="margin-top: 20px" vertical>
              <label htmlFor="password" css="margin-left: 1rem;font-size: 12px">
                Password
              </label>
              <Input
                as="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                name="Password"
                css="height: 40px; margin-top: 5px"
                id="password"
              />
            </Stack>
            {error != null && (
              <Text
                css={`
                  margin-top: 5px;
                  color: ${Colors.red6};
                  margin-left: 10px;
                `}
              >
                Incorrect password
              </Text>
            )}
            <Stack css="margin-top: 25px">
              <Stack css="width: 50%">
                <Button
                  type="button"
                  css="width: 120px; border-color: transparent; margin-right: 5px"
                  variant="ghost"
                  onClick={(e) => {
                    onClose();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
              <Stack css="width: 50%; display: flex; justify-content: center; height:40px;">
                {loading ? (
                  <CircularProgress />
                ) : (
                  <Button css="width: 120px" type="submit" variant="primary">
                    Submit
                  </Button>
                )}
              </Stack>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordDialog;

PasswordDialog.displayName = 'PasswordDialog';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(PasswordDialog);
